import { LearnLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.learn._context";
import type { DrillType } from "@-/dev.improve.haniverse.lib.drill.entities";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { RootContext } from "@-/dev.improve.haniverse.routes._context";
import type { DrillPageSentenceDrill } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.sentence-drill";
import { useContext, useState } from "react";
import { DrillKeyboardActivityContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill/keyboard-activity";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { useDrillKeyboardText } from "#use-drill-keyboard-text";
import type { MorphemeHrefObject } from "@-/dev.improve.haniverse.lib.morpheme.href.entities";

/**
 * The drill session every drill kind shares: the answer box, keyboard, layout heights and glyph
 * replay, plus what the shared chrome needs from the branch it runs in. Each branch gate
 * (`GraphemeDrillGateSuspense` / `WordDrillGateSuspense`) provides it with that branch's exact
 * values; the branch's DATA (slices, backs, sentence) lives in its own branch context.
 */
export function useDrillPageContextValue({
  character,
  drillType,
  expectedAnswerSet,
  morphemeHrefObject,
  playRevealAudio,
  sentenceDrill,
}: {
  character: string;
  drillType: DrillType;
  /** The normalized answers a typed answer is graded against, built by the branch from its data. */
  expectedAnswerSet: ReadonlySet<string>;
  morphemeHrefObject: MorphemeHrefObject;
  /**
   * Speaks the card on reveal. A grapheme drill plays its pinyin; a word drill has no reveal
   * audio. Owned by the branch (ONE player per card) so the reveal mutation — instantiated by
   * several consumers — never builds a player of its own.
   */
  playRevealAudio: () => void;
  /**
   * The sentence-production drills own an in-page state machine instead of grading one answer.
   * Null for every other kind (including every grapheme drill), so consumers ask
   * `sentenceDrill === null` rather than re-listing which kinds are sentence drills.
   */
  sentenceDrill: DrillPageSentenceDrill | null;
}) {
  const [height] = useContextSelectors(RootContext, (c) => c.windowDimensions.height);
  const [isAnswerModeOpen, setIsAnswerModeOpen] = useState(false);
  const [landscapeMainContentHeight, setLandscapeMainContentHeight] = useState(height);
  const [glyphReplayKey, setGlyphReplayKey] = useState(0);
  const keyboardText = useDrillKeyboardText(useContext(DrillKeyboardActivityContext));

  const [answerInputState] = useContextSelectors(LearnLayoutContext, (c) => c.answerInputState);

  const [isPronunciationKeyboardVisible, setIsShuangpinKeyboardVisible] = useDeviceSetting(
    "isPronunciationKeyboardVisible",
  );
  const isShuangpinKeyboardVisible =
    drillType.kind === "pronunciation" && isPronunciationKeyboardVisible;
  const onToggleShuangpinKeyboard = () =>
    setIsShuangpinKeyboardVisible(!isShuangpinKeyboardVisible);

  const submitActionLabel =
    answerInputState.validationState === "incorrect" || answerInputState.input.length === 0
      ? "Reveal"
      : "Check";

  return {
    answerInputState,
    character,
    drillType,
    expectedAnswerSet,
    glyphReplayKey,
    isAnswerModeOpen,
    isShuangpinKeyboardToggleHidden: false,
    isShuangpinKeyboardVisible,
    keyboardText,
    landscapeMainContentHeight,
    morphemeHrefObject,
    onToggleShuangpinKeyboard,
    playRevealAudio,
    sentenceDrill,
    setGlyphReplayKey,
    setIsAnswerModeOpen,
    setIsShuangpinKeyboardVisible,
    setLandscapeMainContentHeight,
    submitActionLabel,
  };
}

export type DrillPageContextValue = ReturnType<typeof useDrillPageContextValue>;
